// Brokerage holdings: a per-position table plus a two-ring allocation donut (outer ring
// = positions by weight, inner ring = grouped by Plaid security type). Rendered only when
// the brokerage reports holdings via Plaid; optional per-position fields degrade to a dash
// when a brokerage omits them (e.g. cost basis). All values come from Plaid's Investments
// holdings, so they are typically end-of-day, not live intraday.

import type { AllocationBreakdown, AllocationSlice, BrokerageHolding } from "../types";
import { money, signedPercent } from "../lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// A fixed palette for donut slices, cycled when there are more slices than colors.
const SLICE_COLORS = [
  "oklch(0.72 0.14 160)",
  "oklch(0.70 0.13 250)",
  "oklch(0.80 0.13 85)",
  "oklch(0.68 0.17 20)",
  "oklch(0.72 0.12 300)",
  "oklch(0.75 0.12 190)",
  "oklch(0.70 0.14 60)",
  "oklch(0.66 0.10 280)",
];

function color(i: number): string {
  return SLICE_COLORS[i % SLICE_COLORS.length];
}

/** SVG arc path for a ring slice from startFrac..endFrac (0..1 of the circle). */
function arc(
  cx: number,
  cy: number,
  rOuter: number,
  rInner: number,
  startFrac: number,
  endFrac: number,
): string {
  const a0 = startFrac * 2 * Math.PI - Math.PI / 2;
  const a1 = endFrac * 2 * Math.PI - Math.PI / 2;
  const large = endFrac - startFrac > 0.5 ? 1 : 0;
  const x0o = cx + rOuter * Math.cos(a0);
  const y0o = cy + rOuter * Math.sin(a0);
  const x1o = cx + rOuter * Math.cos(a1);
  const y1o = cy + rOuter * Math.sin(a1);
  const x0i = cx + rInner * Math.cos(a1);
  const y0i = cy + rInner * Math.sin(a1);
  const x1i = cx + rInner * Math.cos(a0);
  const y1i = cy + rInner * Math.sin(a0);
  return [
    `M ${x0o.toFixed(2)} ${y0o.toFixed(2)}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${x1o.toFixed(2)} ${y1o.toFixed(2)}`,
    `L ${x0i.toFixed(2)} ${y0i.toFixed(2)}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${x1i.toFixed(2)} ${y1i.toFixed(2)}`,
    "Z",
  ].join(" ");
}

/** Render one ring of slices as arcs, returning the SVG paths. */
function ring(slices: AllocationSlice[], rOuter: number, rInner: number, cx: number, cy: number) {
  let acc = 0;
  return slices.map((s, i) => {
    const start = acc;
    acc += s.percent / 100;
    return (
      <path
        key={s.label}
        d={arc(cx, cy, rOuter, rInner, start, Math.min(1, acc))}
        fill={color(i)}
        stroke="var(--background)"
        strokeWidth="1"
      >
        <title>
          {s.label}: {s.percent.toFixed(2)}% ({money(s.value)})
        </title>
      </path>
    );
  });
}

/** The two-ring allocation donut. */
function AllocationDonut({ allocation }: { allocation: AllocationBreakdown }) {
  const size = 240;
  const cx = size / 2;
  const cy = size / 2;
  // Outer ring = positions; inner ring = by type.
  const outer = ring(allocation.positions, 110, 74, cx, cy);
  const inner = ring(allocation.by_type, 70, 38, cx, cy);

  return (
    <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label="allocation by position and type"
        data-testid="allocation-donut"
      >
        {outer}
        {inner}
      </svg>
      {/* Legend: positions (outer ring) with their weights. */}
      <ul className="grid flex-1 grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
        {allocation.positions.map((s, i) => (
          <li key={s.label} className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1.5">
              <span
                className="inline-block size-2 shrink-0 rounded-full"
                style={{ backgroundColor: color(i) }}
              />
              <span className="truncate font-medium">{s.label}</span>
            </span>
            <span className="tabular-nums text-muted-foreground">{s.percent.toFixed(2)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A dash for a missing figure, else the formatted value. */
function num(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return "—";
  return value.toFixed(digits);
}

/** Loading placeholder for the donut + holdings table, shown while a refresh is in
    flight so the section does not sit on stale figures or collapse. */
function HoldingsSkeleton() {
  return (
    <div className="brokerage-holdings space-y-3" data-testid="brokerage-holdings-loading">
      <h4 className="text-sm font-semibold">Holdings</h4>
      <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
        <Skeleton className="size-[240px] rounded-full" />
        <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-4 w-full" />
          ))}
        </div>
      </div>
      <div className="space-y-2 rounded-md border border-border p-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    </div>
  );
}

export function HoldingsSection({
  holdings,
  allocation,
  loading = false,
}: {
  holdings: BrokerageHolding[] | null | undefined;
  allocation: AllocationBreakdown | null | undefined;
  loading?: boolean;
}) {
  if (loading) {
    return <HoldingsSkeleton />;
  }

  if (!holdings || holdings.length === 0) {
    return (
      <div className="brokerage-holdings space-y-2" data-testid="brokerage-holdings-empty">
        <h4 className="text-sm font-semibold">Holdings</h4>
        <p className="text-sm text-muted-foreground">
          This brokerage does not provide position-level holdings through Plaid.
        </p>
      </div>
    );
  }

  return (
    <div className="brokerage-holdings space-y-3" data-testid="brokerage-holdings">
      <h4 className="text-sm font-semibold">Holdings</h4>

      {allocation && <AllocationDonut allocation={allocation} />}

      <div className="rounded-md border border-border [&>[data-slot=table-container]]:overflow-x-hidden md:[&>[data-slot=table-container]]:overflow-x-auto">
        <Table className="block md:table">
          <TableHeader className="hidden md:table-header-group">
            <TableRow>
              <TableHead>Position</TableHead>
              <TableHead className="text-right">Quantity</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead className="text-right">Weight</TableHead>
              <TableHead className="text-right">Gain/loss</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="block divide-y divide-border md:table-row-group">
            {holdings.map((h, i) => (
              <TableRow key={(h.symbol ?? h.name ?? "row") + i} data-testid="holding-row" className="grid grid-cols-2 gap-x-4 gap-y-3 p-3 md:table-row md:p-0">
                <TableCell className="col-span-2 block p-0 md:table-cell md:p-2">
                  <div className="font-medium">{h.symbol ?? "—"}</div>
                  {h.name && (
                    <div className="max-w-[16rem] truncate text-xs text-muted-foreground">
                      {h.name}
                    </div>
                  )}
                </TableCell>
                <TableCell className="block min-w-0 p-0 tabular-nums md:table-cell md:p-2 md:text-right">
                  <span className="mb-0.5 block text-xs text-muted-foreground md:hidden">Quantity</span>
                  <span className="block whitespace-nowrap">{num(h.quantity, 4)}</span>
                </TableCell>
                <TableCell className="block min-w-0 p-0 text-right tabular-nums md:table-cell md:p-2">
                  <span className="mb-0.5 block text-xs text-muted-foreground md:hidden">Value</span>
                  <span className="block whitespace-nowrap">{money(h.value, h.currency ?? undefined)}</span>
                </TableCell>
                <TableCell className="col-span-2 flex min-w-0 items-center justify-between p-0 tabular-nums md:table-cell md:p-2 md:text-right">
                  <span className="text-xs text-muted-foreground md:hidden">Weight</span>
                  {h.weight_percent == null ? "—" : `${h.weight_percent.toFixed(2)}%`}
                </TableCell>
                <TableCell className="col-span-2 flex min-w-0 items-start justify-between gap-3 whitespace-normal p-0 tabular-nums md:table-cell md:p-2 md:text-right md:whitespace-nowrap">
                  <span className="shrink-0 text-xs text-muted-foreground md:hidden">Gain/loss</span>
                  {h.unrealized_gain == null ? (
                    "—"
                  ) : (
                    <span className={`min-w-0 text-right ${h.unrealized_gain >= 0 ? "text-success" : "text-destructive"}`}>
                      {money(h.unrealized_gain, h.currency ?? undefined)}
                      {h.unrealized_gain_percent != null && (
                        <span className="ml-1 whitespace-nowrap text-xs">
                          ({signedPercent(h.unrealized_gain_percent)})
                        </span>
                      )}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-[10px] text-muted-foreground">
        Positions and allocation from your brokerage via Plaid, typically end-of-day. The
        inner ring groups by security type as reported, not by underlying asset class.
      </p>
    </div>
  );
}
